import {
  createContext,
  type JSX,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import {
  type Entitlement,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/nextjs';
import { UserSubscriptionType } from '../services/subscriptions/subscriptions.types';
import { UserPlanType } from '../hooks/useUserSubscription/useUserSubscription.types';
import {
  FronteggFeatureKeys,
  UserEntitlementsContextType,
  UserEntitlementsProviderProps,
} from './userEntitlementsContext.types';

export const UserEntitlementsContext =
  createContext<UserEntitlementsContextType | null>(null);

// Custom hook to use the context
export const useUserEntitlementsContext = (): UserEntitlementsContextType => {
  const context = useContext(UserEntitlementsContext);
  if (!context) {
    throw new Error(
      'useUserEntitlementsContext must be used within a UserEntitlementsProvider',
    );
  }
  return context;
};

// Patient
const usePatientEntitlementsContext = (): UserEntitlementsContextType => {
  const patientFeatureEntitlementOverride = (
    _featureKey: string,
  ): Entitlement => {
    // TODO: Should fetch room slug and call graphl operation to check if patient has access to feature - https://doxyme.atlassian.net/browse/PROD-2650
    throw new Error(
      'usePatientFeatureEntitlements is not implemented for unauthenticated users.',
    );
  };

  const patientPermissionEntitlementOverride = (
    _permissionKey: string,
  ): Entitlement => {
    // TODO: Should fetch room slug and call graphl operation to check if patient has access to permission - https://doxyme.atlassian.net/browse/PROD-2650
    throw new Error(
      'usePatientPermissionEntitlements is not implemented for unauthenticated users.',
    );
  };

  return useMemo(
    () => ({
      plan: undefined,
      isPaidUser: false,
      userPlanType: undefined,
      isLoading: false,
      isSuccess: true,
      isUpgradeButtonVisible: false,
      isSubscriptionTabVisible: false,
      hasProPlanSubscription: false,
      isTrustedCustomer: false,
      // Instead of throwing, we return a default entitlement object because we have some components in patient context that are not patient-specific
      useFeatureEntitlements: () => ({
        isEntitled: false,
        justification: undefined,
      }),
      usePermissionEntitlements: () => ({
        isEntitled: false,
        justification: undefined,
      }),
      usePatientFeatureEntitlements: patientFeatureEntitlementOverride,
      usePatientPermissionEntitlements: patientPermissionEntitlementOverride,
    }),
    [],
  );
};

// Authenticated provider - uses frontegg entitlements
const useAuthenticatedUserEntitlementsContext =
  (): UserEntitlementsContextType => {
    const { isEntitled } = useFeatureEntitlements(FronteggFeatureKeys.ProPlan);
    const { isEntitled: hasProPlanSubscription } = useFeatureEntitlements(
      FronteggFeatureKeys.ProPlanSubscription,
    );
    const { isEntitled: isTrustedCustomer } = useFeatureEntitlements(
      FronteggFeatureKeys.TrustedCustomer,
    );

    // Custom hooks for patient-specific entitlements - always throw for providers
    const useAuthenticatedPatientFeatureEntitlement = (
      _featureKey: string,
    ): Entitlement => {
      throw new Error(
        'usePatientFeatureEntitlements should only be used by patients. Providers should use useFeatureEntitlements instead.',
      );
    };

    const useAuthenticatedPatientPermissionEntitlement = (
      _permissionKey: string,
    ): Entitlement => {
      throw new Error(
        'usePatientPermissionEntitlements should only be used by patients. Providers should use usePermissionEntitlements instead.',
      );
    };

    return useMemo<UserEntitlementsContextType>(
      () => ({
        plan: isEntitled ? UserSubscriptionType.Pro : UserSubscriptionType.Free,
        isPaidUser: isEntitled,
        userPlanType: isEntitled
          ? UserPlanType.Professional
          : UserPlanType.Free,
        isLoading: false,
        isSuccess: true,
        isUpgradeButtonVisible: !isEntitled,
        isSubscriptionTabVisible: true,
        hasProPlanSubscription,
        isTrustedCustomer,
        // Expose the frontegg entitlements hooks here so app components can pivot from isPaidUser to specific entitlement checks
        useFeatureEntitlements, // use frontegg sdk
        usePermissionEntitlements, // use frontegg sdk
        usePatientFeatureEntitlements:
          useAuthenticatedPatientFeatureEntitlement, // throws
        usePatientPermissionEntitlements:
          useAuthenticatedPatientPermissionEntitlement, // throws
      }),
      [hasProPlanSubscription, isEntitled, isTrustedCustomer],
    );
  };

// Helper component that calls Frontegg hooks only when rendered
// Rendered conditionally as a SIBLING (not wrapper) so it doesn't affect children mounting
const AuthenticatedEntitlementsUpdater = ({
  onUpdate,
}: {
  onUpdate: (value: UserEntitlementsContextType) => void;
}): null => {
  const contextValue = useAuthenticatedUserEntitlementsContext();

  useEffect(() => {
    onUpdate(contextValue);
  }, [contextValue]);

  return null; // Renders nothing
};

// Main provider that conditionally renders based on authentication status
// ALWAYS use the same Provider component - never switches
export const UserEntitlementsProvider = ({
  children,
  isAuthenticated,
}: UserEntitlementsProviderProps): JSX.Element => {
  // Always call the patient context hook (doesn't use Frontegg)
  const patientContextValue = usePatientEntitlementsContext();

  // State to hold authenticated context value
  const [authenticatedContextValue, setAuthenticatedContextValue] =
    useState<UserEntitlementsContextType>(patientContextValue);

  const contextValue = isAuthenticated
    ? authenticatedContextValue
    : patientContextValue;

  return (
    <UserEntitlementsContext.Provider value={contextValue}>
      {/* Conditionally render updater, to not call frontegg hooks when not authenticated */}
      {isAuthenticated && (
        <AuthenticatedEntitlementsUpdater
          onUpdate={setAuthenticatedContextValue}
        />
      )}
      {children}
    </UserEntitlementsContext.Provider>
  );
};
